/**
 * shared/space_economy.ts — CLIENT MIRROR of server/src/lib/space_economy.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers). Space Wave 3 — the first supply route: NPC firms, finite belt
 * deposits, shipments down the lane, the ambush/escort odds, the yard's
 * shortage price, patrol readiness. Pure.
 */
import { warH } from './space_stations';
import { oresForTier, sysTierOf } from './space_industry';
import type { SystemSector } from './galaxy';

// ── FIRMS (THE FIRM LAW) ────────────────────────────────────────────────────
export interface FirmDef {
  firmId: bigint; key: string; name: string; civ: string;
  kind: 'miner' | 'yard'; stationKey: string; beltKey: string; hullsTarget: number;
}
/** The firms of the two lanes (THE TWO-LANE LAW): each civ has a miner
 *  hauling to ITS yard. Aurelian: Waystation Aurel (Kessel Run Scar) → Aurel
 *  Prime. Borean: Farlight Terminal (The Long Dark) → Nova Aurel Station.
 *  firmId is the space_firm PK; a miner ships to the yard of its own civ. */
export const FIRM_DEFS: readonly FirmDef[] = [
  { firmId: 1n, key: 'aurel_mining', name: 'Aurel Mining', civ: 'aurelian', kind: 'miner', stationKey: 'sys0:st0', beltKey: 'poi8', hullsTarget: 0 },
  { firmId: 2n, key: 'aurel_yards', name: 'Aurel Prime Yards', civ: 'aurelian', kind: 'yard', stationKey: 'sys0:st2', beltKey: '', hullsTarget: 6 },
  { firmId: 3n, key: 'borean_mining', name: 'Borean Deep Mining', civ: 'borean', kind: 'miner', stationKey: 'sys0:st3', beltKey: 'poi3', hullsTarget: 0 },
  { firmId: 4n, key: 'borean_yards', name: 'Borean Crown Yards', civ: 'borean', kind: 'yard', stationKey: 'sys0:st1', beltKey: '', hullsTarget: 6 },
];
/** The yard a miner ships to: the yard of its own civ (null for a yard). */
export function yardDefFor(civ: string): FirmDef | null {
  return FIRM_DEFS.find((f) => f.civ === civ && f.kind === 'yard') ?? null;
}
/** One-time seed faucet per firm row (PLATFORM → firm, reason firm_seed). */
export const FIRM_SEED_SOV = 60_000n;
/** The bits of a firm's deterministic Identity (the server wraps them in `new Identity(bits)`). */
export function firmIdentityBits(firmId: bigint): bigint { return 0x5f1a_0000n + firmId; }

// ── DEPOSITS (THE DEPOSIT LAW) ──────────────────────────────────────────────
export interface BeltSpec { key: string; name: string; pos: [number, number, number]; radius: number }
/** The Commons Reach's nine belts in space_sector.ts POI order — the index IS
 *  the client's collider-group index, which is what fieldOre hashes on. */
export const SYS0_BELTS: readonly BeltSpec[] = [
  { key: 'poi1', name: 'The Shard Belt', pos: [-320_000, -26_000, 430_000], radius: 7_000 },
  { key: 'poi3', name: 'The Long Dark', pos: [-620_000, 90_000, -360_000], radius: 8_500 },
  { key: 'poi8', name: 'Kessel Run Scar', pos: [300_000, -40_000, -350_000], radius: 15_000 },
  { key: 'poi13', name: 'Homefield Belt', pos: [-14_000, -700, 11_500], radius: 3_200 },
  { key: 'poi16', name: 'The Ashen Reach', pos: [-150_000, 40_000, 1_150_000], radius: 18_000 },
  { key: 'poi17', name: 'Coldline Shoals', pos: [950_000, -180_000, 450_000], radius: 13_000 },
  { key: 'poi18', name: "The Widow's Teeth", pos: [-1_350_000, -60_000, -150_000], radius: 21_000 },
  { key: 'poi19', name: 'Emberfall Drift', pos: [200_000, 260_000, 1_500_000], radius: 16_000 },
  { key: 'poi20', name: 'The Graveyard Ring', pos: [1_400_000, 60_000, 300_000], radius: 24_000 },
];
/** A system's belts: the Commons list, or a derived system's fields as f<i>
 *  (the same order the client builds its collider groups in). */
export function beltsOf(systemId: string, sector: SystemSector | null): BeltSpec[] {
  if (systemId === 'sys0') return [...SYS0_BELTS];
  if (!sector || !/^sys\d{1,3}$/.test(systemId)) return [];
  return sector.fields.map((f, i) => ({ key: `f${i}`, name: `Field ${i + 1}`, pos: [f.pos[0], f.pos[1], f.pos[2]] as [number, number, number], radius: f.radius }));
}
/** The client's fieldOre rule — which ore a belt carries (display; the firm mines its own). */
export function beltOreKey(systemId: string, idx: number): string {
  const pool = oresForTier(sysTierOf(systemId));
  return pool[Math.floor(warH(`${systemId}|field${idx}`) * pool.length) % pool.length].key;
}
export function depositCapacity(radius: number): number { return Math.round(1800 + radius / 10); }
/** Whole hours renew this fraction of the capacity each. */
export const DEPOSIT_RENEW_FRAC_PER_H = 0.06;
/** The beam meters the belt whose centre is within this of the pilot (the
 *  client's collider reach is radius × 1.15..1.2 + 900; a margin on top). */
export function depositReach(radius: number): number { return radius * 1.2 + 1_300; }
/** The renewal since the anchor: whole hours only (the fraction stays banked
 *  by leaving the anchor where it was). */
export function depositRenewed(remaining: number, capacity: number, hoursSince: number): { remaining: number; hours: number } {
  const hours = Math.max(0, Math.floor(hoursSince));
  if (hours === 0) return { remaining, hours: 0 };
  return { remaining: Math.min(capacity, remaining + Math.floor(hours * capacity * DEPOSIT_RENEW_FRAC_PER_H)), hours };
}

// ── THE LANE (THE LANE / AMBUSH / ESCORT / ARRIVAL LAWS) ────────────────────
export const MINER_EXTRACT_U_PER_H = 90;
export const MINER_ORE_KEY = 'drift_ferrite';
export const MINER_FAMILY = 'ferrite_ore';
export const SHIPMENT_LOT_U = 60;
export const SHIPMENT_SPEED_MPS = 320;   // the cosmetic convoys' speed
export const AMBUSH_WINDOW = { start: 0.35, end: 0.7 } as const;
export const AMBUSH_P_UNESCORTED = 0.45;
export const AMBUSH_P_ESCORTED = 0.10;
export const ESCORT_RANGE_M = 4_000;
export const ESCORT_REWARD_SOV = 150n;
export const SHIPMENT_REAP_S = 24 * 3600;
export function shipmentFlightS(from: readonly number[], to: readonly number[]): number {
  return Math.max(60, Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]) / SHIPMENT_SPEED_MPS);
}
export function shipmentFrac(departMicros: bigint, arriveMicros: bigint, nowMicros: bigint): number {
  const span = Number(arriveMicros - departMicros);
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, Number(nowMicros - departMicros) / span));
}
export function shipmentPosAt(from: readonly number[], to: readonly number[], departMicros: bigint, arriveMicros: bigint, nowMicros: bigint): [number, number, number] {
  const f = shipmentFrac(departMicros, arriveMicros, nowMicros);
  return [from[0] + (to[0] - from[0]) * f, from[1] + (to[1] - from[1]) * f, from[2] + (to[2] - from[2]) * f];
}
/** The hashed moment inside the ambush window, in micros. */
export function ambushMoment(departMicros: bigint, flightS: number, roll01: number): bigint {
  const at = (AMBUSH_WINDOW.start + roll01 * (AMBUSH_WINDOW.end - AMBUSH_WINDOW.start)) * flightS;
  return departMicros + BigInt(Math.round(at * 1_000_000));
}
export function ambushHit(roll: number, escorted: boolean): boolean {
  return roll < (escorted ? AMBUSH_P_ESCORTED : AMBUSH_P_UNESCORTED);
}

// ── THE YARD (THE YARD LAW) ─────────────────────────────────────────────────
export const HULL_ORE_U = 40;
export const HULLS_MAX = 12;
export const SUPPLY_GOAL_U = 120;
export const SUPPLY_REWARD_MULT = 1.5;
export const SUPPLY_SHORT_HOURS = 2;
export const PRICE_MOD_PER_SHORT_HOUR_PCT = 12;
export const PRICE_MOD_MAX_PCT = 160;
/** The shortage counter is the price cap's own clock: it never runs past the hour the cap is reached, and a
 *  delivery walks it back an hour at once (a yard 55 hours short eased nothing for a day — 2026-09-17). */
export const SHORT_HOURS_CAP = (PRICE_MOD_MAX_PCT - 100) / PRICE_MOD_PER_SHORT_HOUR_PCT;
/** The yard's ore price multiplier from its short hours (hundredths, no float drift). */
export function priceMult(shortHours: number): number {
  return Math.min(PRICE_MOD_MAX_PCT, 100 + PRICE_MOD_PER_SHORT_HOUR_PCT * Math.max(0, Math.floor(shortHours))) / 100;
}
export function applyPriceMod(unit: bigint, mult: number): bigint {
  return mult === 1 ? unit : BigInt(Math.max(1, Math.round(Number(unit) * mult)));
}

// ── READINESS (THE READINESS LAW) ───────────────────────────────────────────
/** The post each civ's yard staffs: the civ's side of the POI that carries its yard's station
 *  (Aurel Prime for the Aurelian yard, Nova Aurel for the Borean one). */
export interface ReadinessPost { poi: string; side: 0 | 1; yardId: bigint }
export const READINESS_POSTS: Readonly<Record<'aurelian' | 'borean', ReadinessPost>> = {
  aurelian: { poi: 'poi12', side: 0, yardId: 2n },
  borean: { poi: 'poi4', side: 1, yardId: 4n },
};
/** The post a (poi, side) pair staffs, if any yard staffs it. */
export function readinessPostFor(poiKey: string, side: number): ReadinessPost | null {
  for (const p of Object.values(READINESS_POSTS)) if (p.poi === poiKey && p.side === side) return p;
  return null;
}
/** The Aurelian post the first yard's hulls staff: Aurel Prime (kept for the Wave 3 readers). */
export const READINESS_POI = READINESS_POSTS.aurelian.poi;
export function patrolReadiness(hulls: number, target: number): number {
  if (target <= 0) return 1;
  return Math.min(1.5, Math.max(0.35, hulls / target));
}
export function patrolShipsFor(baseShips: number, readiness: number): number {
  return Math.max(1, Math.round(baseShips * readiness));
}

// ── THE RAIDER LAW (real hulls on the lane) ─────────────────────────────────
export const AMBUSH_RAIDERS = 3;
export const AMBUSH_FIGHT_S = 90;
export const AMBUSH_KILL_RANGE_M = 4_000;
export const ESCORT_ATTRITION_S = 30;
export const RAID_KEY_RE = /^raid\d{1,9}:\d$/;
export type AmbushOutcome = 'beaten' | 'took' | 'passed';
/** At the end of the fight: none left → beaten; two or more → they take the run; one → the old odds. */
export function ambushOutcome(raidersAlive: number, escorted: boolean, roll: number): AmbushOutcome {
  if (raidersAlive <= 0) return 'beaten';
  if (raidersAlive >= 2) return 'took';
  return ambushHit(roll, escorted) ? 'took' : 'passed';
}

// ── THE PORT (the planetary port, 2026-09-17) ───────────────────────────────
/** One haul: a freighter hold's worth (the sky lane's CARGO_QTY_MAX). */
export const PORT_LOT_U = 24;
/** Lifetime export cap per port per economy generation (ten hauls). */
export const PORT_LIFETIME_CAP_U = 240;
/** The coin book's working-capital target (THE TWO-BOOKS LAW). */
export const PORT_COIN_TARGET = 60n;
/** The market fund never funds the port below this (= MARKET_FUND_SEED). */
export const PORT_FUND_FLOOR = 400n;
/** The credit wallet's target; the civ's yard tops it up from its surplus. */
export const PORT_CREDIT_TARGET = 2_000n;
/** What a yard keeps before it capitalises its port. */
export const YARD_CAPITAL_RESERVE = 4_000n;
/** A haul offer or ride lapses after six hours. */
export const PORT_HAUL_TTL_S = 6 * 3600;
/** Imported ore enters the shelf this many units per hour. */
export const PORT_RELEASE_U = 12;
/** The ore family the ports trade (1:1 with the Commons `ore` shelf). */
export const PORT_FAMILY = MINER_FAMILY;
/** The port firm ids per civ (the yards are 2 and 4; the miners 1 and 3). */
export function portFirmIdFor(civ: string): bigint { return civ === 'borean' ? 6n : 5n; }
/** Ore the town keeps for its own smelting and smithing: four per ore-consuming slot plus two smith runs. */
export function oreReserve(oreSlots: number): number { return 4 * Math.max(0, oreSlots) + 8; }
/** The hauler's reward: thirty percent of the expected yard sale, twenty credits at least. */
export function haulReward(qty: number, unitSov: bigint): bigint {
  return BigInt(Math.max(20, Math.round(qty * Number(unitSov) * 0.3)));
}
/** THE RELEASE LAW's plan: units the shelf takes this hour and the coins the fund pays — a dry fund buys fewer. */
export function releasePlan(shelf: number, reserve: number, held: number, fundCash: bigint, price: number): { units: number; coins: bigint } {
  const want = Math.max(0, Math.min(PORT_RELEASE_U, held, Math.ceil(2 * reserve - shelf)));
  if (want <= 0) return { units: 0, coins: 0n };
  const unit = BigInt(Math.max(1, Math.round(price)));
  const spendable = fundCash > PORT_FUND_FLOOR ? fundCash - PORT_FUND_FLOOR : 0n;
  const affordable = Number(spendable / unit);
  const units = Math.min(want, affordable);
  return { units, coins: BigInt(units) * unit };
}
/** The port city of a civ: its lowest-id CITY (spaceports serve the cities). */
export function portCityPick<S extends { id: number; tier: string }>(sites: readonly S[], civOf: (s: S) => string | null, civ: string): S | null {
  let best: S | null = null;
  for (const s of sites) {
    if (s.tier !== 'city' || civOf(s) !== civ) continue;
    if (!best || s.id < best.id) best = s;
  }
  return best;
}

// ── THE DEPTH LAW (the exchange is not bottomless) ──────────────────────────
/** Units per (station, commodity, epoch) that move the mark all the way to its floor/ceiling. */
export const DEPTH_UNITS = 1600;
export const DEPTH_SELL_FLOOR = 0.5;
export const DEPTH_BUY_CEIL = 1.6;
/** The mark's multiplier after `units` were already sold (side 'sell') or bought (side 'buy') this epoch. */
export function depthMult(side: 'buy' | 'sell', units: number): number {
  const u = Math.max(0, units);
  if (side === 'sell') return Math.max(DEPTH_SELL_FLOOR, 1 - u / DEPTH_UNITS);
  return Math.min(DEPTH_BUY_CEIL, 1 + u / DEPTH_UNITS);
}
